import { NetworkType, useFormatAmount, useIntl } from '@capsicum/common';
import { Alert, Button, ButtonSection, useRouter } from '@capsicum/common-web';
import { AlertIcon } from '@capsicum/common-web/icons';
import {
	CURRENCY_LABELS,
	CurrencyType,
	RECEIVE_NETWORK_LABELS,
	RECEIVE_NETWORK_SHORTCODES,
	type ReceiveNetworks,
	TypeCurrency,
	useWalletWithdrawalMinimumAmount,
	useWithdrawDataAtom
} from '@capsicum/features/wallet/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { type NetworkCardItem, NetworkSelectionListCard } from '../../ui';

const networks: Array<NetworkCardItem> = [
	{
		label: RECEIVE_NETWORK_LABELS[NetworkType.CC2 as ReceiveNetworks],
		name: NetworkType.CC2 as ReceiveNetworks
	},
	{
		isNotSupported: true,
		label: RECEIVE_NETWORK_LABELS[NetworkType.CC1 as ReceiveNetworks],
		name: NetworkType.CC1 as ReceiveNetworks,
		shortCode: RECEIVE_NETWORK_SHORTCODES[NetworkType.CC1 as ReceiveNetworks]
	}
];

export const NetworkFantokens = () => {
	const { push } = useRouter();
	const [selectedNetwork, setSelectedNetwork] = useState<string | undefined>(undefined);
	const [isButtonDisabled, setButtonDisabled] = useState<boolean>(true);
	const { formatAmount } = useFormatAmount();
	const { setSelectedNetworkType } = useWithdrawDataAtom();
	const { isLoading: minFTLoading, minAmount: minFT } = useWalletWithdrawalMinimumAmount(TypeCurrency.VoteToken);

	const getNetworkStatus = useCallback(
		(networkId: string) => {
			if (selectedNetwork === networkId) {
				return 'selected';
			}

			return 'unselected';
		},
		[selectedNetwork]
	);

	useEffect(() => {
		if (!selectedNetwork) {
			return;
		}
		setButtonDisabled(false);
	}, [selectedNetwork, setSelectedNetwork]);

	const { $t } = useIntl();

	const title = $t('wallet.withdraw.networks.title');
	const warning = $t('wallet.withdraw.networks.alertFT');
	const button = $t('wallet.withdraw.networks.button');

	const networkCards = useMemo(
		() =>
			networks.map(network => {
				const networkWithMinimum = {
					...network,
					isMinAmountLoading: minFTLoading,
					minAmount: `${formatAmount(minFT)} ${CURRENCY_LABELS[CurrencyType.FANTOKENS]}`
				};

				return (
					<NetworkSelectionListCard
						item={networkWithMinimum}
						key={networkWithMinimum.name}
						selectNetwork={setSelectedNetwork}
						status={getNetworkStatus(networkWithMinimum.name)}
					/>
				);
			}),
		[formatAmount, getNetworkStatus, minFT, minFTLoading]
	);

	return (
		<S.Container>
			<S.Title>{title}</S.Title>
			{networkCards}
			<S.AlertWrapper
				closable={false}
				icon={AlertIcon}
				subTitle={warning}
				testID="withdrawFT-selectNetwork-warning"
				title=""
				type="important"
			/>
			<ButtonSection>
				<Button
					color="brand"
					disabled={minFTLoading || isButtonDisabled}
					isLoading={minFTLoading}
					testID="withdrawFT-selectNetwork-next"
					text={button}
					variant="default"
					onClick={() => {
						setSelectedNetworkType(selectedNetwork || '');
						push(`/wallet/withdraw/fantokens/amount`);
					}}
				/>
			</ButtonSection>
		</S.Container>
	);
};

namespace S {
	export const Container = styled.section`
		margin-top: ${({ theme }) => theme.spacings.xxlarge};
		display: flex;
		gap: ${({ theme }) => theme.spacings.small};
		flex-flow: column;
		width: 100%;
		align-items: center;
		padding: ${({ theme }) => `0 ${theme.spacings.xsmall}`};
	`;
	export const AlertWrapper = styled(Alert)`
		width: 100%;
	`;
	export const Title = styled.h1`
		width: 100%;
		${({ theme }) => theme.fonts.headings.title5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		text-align: left;
		margin: 0;
	`;
	export const Subtitle = styled.h2`
		width: 100%;
		${({ theme }) => theme.fonts.text.text3};
		text-align: left;
		margin: 0;
		display: flex;
		align-items: center;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;
}
